import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react';

import { clientLog } from '../lib/clientLog';
import type { OnboardingDraft, WizardStep } from './types';

// Generic wizard chrome: progress dots, current step content, navigation
// buttons. Steps own their content; the wizard owns navigation and the
// submit handler. The draft itself lives in the parent so async sources
// (geo lookup, prior session) can patch it without remounting the wizard.

export interface WizardProps {
  steps: readonly WizardStep[];
  draft: OnboardingDraft;
  update: (patch: Partial<OnboardingDraft>) => void;
  onSubmit: (draft: OnboardingDraft) => Promise<void> | void;
  submittingLabelKey?: string;
  submitErrorCode?: string | null;
  // Imperative "go to this step" signal. When the object identity changes
  // (parent sets a fresh {stepId, nonce}), the wizard navigates to the step
  // with that id. Used by the submit handler to bounce the user back to the
  // Import step when a late preflight surfaces fixable row errors — otherwise
  // the error UI (which only lives in that step) would be invisible.
  jumpToStep?: { stepId: string; nonce: number } | null;
}

export function Wizard({
  steps,
  draft,
  update,
  onSubmit,
  submittingLabelKey = 'common.submitting',
  submitErrorCode,
  jumpToStep,
}: WizardProps) {
  const { t } = useTranslation('common');
  const [stepIndex, setStepIndex] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  // Honour an imperative jump from the parent (e.g. submit handler bouncing
  // back to the Import step on late validation errors). Keyed on nonce so the
  // same target step can be requested twice in a row and still navigate.
  useEffect(() => {
    if (!jumpToStep) return;
    const idx = steps.findIndex((s) => s.id === jumpToStep.stepId);
    if (idx >= 0) setStepIndex(idx);
  }, [jumpToStep, steps]);

  const step = steps[stepIndex];
  if (!step) return null; // Defensive — steps array is required to be non-empty.

  const isLast = stepIndex === steps.length - 1;
  const canContinue = step.isValid(draft);

  const back = () => setStepIndex((i) => Math.max(0, i - 1));

  const next = async () => {
    if (!canContinue) return;
    if (isLast) {
      setSubmitting(true);
      try {
        await onSubmit(draft);
      } finally {
        setSubmitting(false);
      }
      return;
    }
    setStepIndex((i) => i + 1);
  };

  return (
    <section className="wizard">
      <ol className="wizard__progress" aria-label={t('onboarding.progressLabel')}>
        {steps.map((s, i) => (
          <li
            key={s.id}
            className={
              'wizard__dot' +
              (i === stepIndex ? ' wizard__dot--active' : '') +
              (i < stepIndex ? ' wizard__dot--done' : '')
            }
            aria-current={i === stepIndex ? 'step' : undefined}
          >
            <span className="wizard__dot-num">{i + 1}</span>
            <span className="wizard__dot-label">{t(s.titleKey)}</span>
          </li>
        ))}
      </ol>

      <header className="wizard__header">
        <h2>{t(step.titleKey)}</h2>
        {step.descriptionKey && <p>{t(step.descriptionKey)}</p>}
      </header>

      <Sentry.ErrorBoundary
        // Reset the boundary when the user navigates between steps so a
        // crash on one step doesn't permanently jam another. The draft
        // state lives in Onboarding (parent), so the user keeps their
        // progress when the boundary resets.
        key={step.id}
        beforeCapture={(scope) => {
          scope.setTag('onboarding.step', step.id);
        }}
        onError={(err) => {
          clientLog.error(`onboarding: step ${step.id} crashed`, {
            error: err,
            fields: { step: `onboarding/${step.id}`, stage: 'render' },
          });
        }}
        fallback={({ resetError }) => (
          <div role="alert" className="error" style={{ padding: '1rem' }}>
            <p style={{ marginBottom: '0.5rem' }}>
              {t('onboarding.stepCrashed.body')}
            </p>
            <button
              type="button"
              className="btn btn--primary"
              onClick={() => resetError()}
            >
              {t('onboarding.stepCrashed.retry')}
            </button>
          </div>
        )}
      >
        <step.Component draft={draft} update={update} />
      </Sentry.ErrorBoundary>

      {submitErrorCode && (
        <p role="alert" className="error">
          {t(`onboarding.error.${submitErrorCode}`, { defaultValue: t('onboarding.error.unknown') })}
        </p>
      )}

      <footer className="wizard__nav">
        <button
          type="button"
          className="btn btn--ghost"
          onClick={back}
          disabled={stepIndex === 0 || submitting}
        >
          {t('common.back')}
        </button>
        <button
          type="button"
          className="btn btn--primary"
          onClick={() => void next()}
          disabled={!canContinue || submitting}
        >
          {submitting
            ? t(submittingLabelKey)
            : isLast
              ? t('onboarding.finish')
              : t('common.continue')}
        </button>
      </footer>
    </section>
  );
}
